import { useEffect, useState, type FormEvent } from 'react';
import { Seitenkopf } from '../components/Seitenkopf';
import { Badge } from '../components/ui/Badge';
import type { BadgeTon } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Input';
import { IconHaken, IconHinweis, IconPlus, IconWartung } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { DEMO_OBJEKTE } from '../lib/demodaten';
import type { Ampel } from '../lib/startseite-daten';
import {
  baueAnlagenbuch,
  demoAnlagenbuch,
  type AnlageAnsicht,
  type ApiAnlage,
  type ApiFaellig,
  type ApiObjektRef,
  type PflichtAnsicht,
} from '../lib/anlagenbuch-daten';
import { formatDatum } from '../lib/format';
import { GebaeudeCheck } from './GebaeudeCheck';
import { Select } from '../components/ui/Input';

const AMPEL_TON: Record<Ampel, BadgeTon> = { rot: 'gefahr', gelb: 'warnung', gruen: 'erfolg' };
const AMPEL_TEXT: Record<Ampel, string> = { rot: 'Überfällig', gelb: 'Bald fällig', gruen: 'Im Plan' };

const VERBINDLICHKEIT_TON: Record<string, BadgeTon> = {
  gesetz: 'info',
  regel_der_technik: 'neutral',
  vertraglich: 'neutral',
  empfehlung: 'neutral',
};
const VERBINDLICHKEIT_TEXT: Record<string, string> = {
  gesetz: 'Gesetzliche Pflicht',
  regel_der_technik: 'Regel der Technik',
  vertraglich: 'Vertraglich',
  empfehlung: 'Empfehlung',
};

const DEMO_GEWERKE = [
  { code: 'heizung', bezeichnung: 'Heizung' },
  { code: 'trinkwasser', bezeichnung: 'Trinkwasser' },
  { code: 'klima', bezeichnung: 'Klima- und Kältetechnik' },
  { code: 'aufzug', bezeichnung: 'Aufzug' },
  { code: 'elektro', bezeichnung: 'Elektrotechnik' },
  { code: 'brandschutz', bezeichnung: 'Brandschutz' },
  { code: 'blitzschutz', bezeichnung: 'Blitzschutz' },
];

interface Auswahl {
  code: string;
  bezeichnung: string;
}

function rang(a: Ampel): number {
  return a === 'rot' ? 0 : a === 'gelb' ? 1 : 2;
}

export function Anlagenbuch() {
  const [daten, setDaten] = useState<AnlageAnsicht[] | null>(DEMO ? demoAnlagenbuch() : null);
  const [objekte, setObjekte] = useState<ApiObjektRef[]>(
    DEMO ? DEMO_OBJEKTE.map((o) => ({ id: o.id, bezeichnung: o.bezeichnung })) : [],
  );
  const [gewerke, setGewerke] = useState<Auswahl[]>(DEMO_GEWERKE);
  const [laden, setLaden] = useState(!DEMO);
  const [fehler, setFehler] = useState<string | null>(null);
  const [formOffen, setFormOffen] = useState(false);
  const [checkOffen, setCheckOffen] = useState(false);
  const [erfolg, setErfolg] = useState<string | null>(null);

  async function laden_() {
    if (DEMO) return;
    setLaden(true);
    try {
      const [a, f, o, g] = await Promise.all([
        api.get<{ anlagen: ApiAnlage[] }>('/anlagen'),
        api.get<{ pflichten: ApiFaellig[] }>('/pflichten/faellig'),
        api.get<{ objekte: ApiObjektRef[] }>('/objekte'),
        api.get<{ gewerke: Auswahl[] }>('/gewerke'),
      ]);
      setDaten(baueAnlagenbuch(a.anlagen, f.pflichten, o.objekte));
      setObjekte(o.objekte);
      setGewerke(g.gewerke);
      setFehler(null);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Das Anlagenbuch konnte nicht geladen werden.');
    } finally {
      setLaden(false);
    }
  }

  useEffect(() => {
    void laden_();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function nachAnlegen(hinweis: string, demoAnlage?: AnlageAnsicht) {
    setErfolg(hinweis);
    setFormOffen(false);
    if (DEMO && demoAnlage) {
      setDaten((b) => [demoAnlage, ...(b ?? [])]);
    } else {
      void laden_();
    }
  }

  const objekteNamen = daten ? Array.from(new Set(daten.map((a) => a.objekt))) : [];

  return (
    <div>
      <Seitenkopf
        titel="Anlagenbuch"
        beschreibung="Jede Anlage einmal erfassen — die gesetzlichen Prüf- und Wartungsfristen leitet das System automatisch aus dem hinterlegten Prüfkatalog ab. Sie müssen das Gesetz nicht kennen."
        aktion={
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => {
                setCheckOffen(true);
                setFormOffen(false);
                setErfolg(null);
              }}
            >
              Gebäude-Check
            </Button>
            <Button
              variante="sekundaer"
              icon={<IconPlus groesse={18} />}
              onClick={() => {
                setFormOffen((o) => !o);
                setCheckOffen(false);
              }}
            >
              Anlage erfassen
            </Button>
          </div>
        }
      />

      {erfolg && (
        <p className="mb-4 flex items-start gap-2 rounded-lg bg-success-soft px-3 py-2 text-sm text-success-soft-fg">
          <span className="mt-0.5 shrink-0">
            <IconHaken groesse={16} />
          </span>
          {erfolg}
        </p>
      )}

      {checkOffen && (
        <GebaeudeCheck
          objekte={objekte}
          onAbbruch={() => setCheckOffen(false)}
          onFertig={() => {
            setCheckOffen(false);
            void laden_();
          }}
        />
      )}

      {formOffen && (
        <AnlageFormular
          objekte={objekte}
          gewerke={gewerke}
          onAngelegt={nachAnlegen}
          onAbbruch={() => setFormOffen(false)}
        />
      )}

      {checkOffen ? null : laden && !daten ? (
        <p className="text-sm text-fg-muted">Anlagenbuch wird geladen …</p>
      ) : fehler && !daten ? (
        <Card>
          <p className="text-sm text-danger">{fehler}</p>
        </Card>
      ) : daten && daten.length === 0 ? (
        <EmptyState
          icon={<IconWartung groesse={22} />}
          titel="Noch keine Anlage erfasst"
          beschreibung="Der Gebäude-Check erfasst den Anlagenbestand in wenigen Schritten und ordnet die Prüf- und Wartungspflichten automatisch zu."
          aktion={<Button onClick={() => setCheckOffen(true)}>Gebäude-Check starten</Button>}
        />
      ) : (
        <div className="space-y-6">
          {objekteNamen.map((objekt) => (
            <section key={objekt}>
              <h2 className="mb-2 px-1 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
                {objekt}
              </h2>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {(daten ?? [])
                  .filter((a) => a.objekt === objekt)
                  .map((anlage) => (
                    <AnlageKarte key={anlage.id} anlage={anlage} />
                  ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <p className="mt-6 flex items-start gap-2 rounded-lg bg-accent-soft px-3 py-2 text-sm text-accent-soft-fg">
        <span className="mt-0.5 shrink-0">
          <IconHinweis groesse={16} />
        </span>
        Die Fristen sind eine Vorbelegung aus dem Prüfkatalog und jederzeit überschreibbar — keine
        Rechtsberatung. Anerkannte Regeln der Technik sind als solche gekennzeichnet, nicht als
        gesetzliche Pflicht.
      </p>
    </div>
  );
}

function AnlageKarte({ anlage }: { anlage: AnlageAnsicht }) {
  const pflichten = [...anlage.pflichten].sort((a, b) => rang(a.ampel) - rang(b.ampel));
  return (
    <Card ohnePolster>
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2.5">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent-soft-fg">
              <IconWartung groesse={17} />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-fg">{anlage.bezeichnung}</p>
              <p className="mt-0.5 truncate text-xs text-fg-muted">
                {[anlage.typ, anlage.hersteller, anlage.baujahr ? `Baujahr ${anlage.baujahr}` : null]
                  .filter(Boolean)
                  .join(' · ') || '—'}
              </p>
            </div>
          </div>
          <Badge ton="neutral">{anlage.gewerk}</Badge>
        </div>
      </div>

      <div className="px-4 py-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-fg-subtle">
          Abgeleitete Prüf- und Wartungspflichten
        </p>
        {pflichten.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <IconHaken groesse={16} />
            Für diese Anlage sind derzeit keine Pflichten fällig.
          </div>
        ) : (
          <ul className="space-y-2.5">
            {pflichten.map((p) => (
              <PflichtZeile key={p.id} p={p} />
            ))}
          </ul>
        )}
        {anlage.standort && <p className="mt-3 text-xs text-fg-subtle">Standort: {anlage.standort}</p>}
      </div>
    </Card>
  );
}

function PflichtZeile({ p }: { p: PflichtAnsicht }) {
  return (
    <li className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium text-fg">{p.bezeichnung}</span>
          <Badge ton={AMPEL_TON[p.ampel]} punkt>
            {AMPEL_TEXT[p.ampel]}
          </Badge>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {p.verbindlichkeit && (
            <Badge ton={VERBINDLICHKEIT_TON[p.verbindlichkeit] ?? 'neutral'}>
              {VERBINDLICHKEIT_TEXT[p.verbindlichkeit] ?? p.verbindlichkeit}
            </Badge>
          )}
          {p.rechtsgrundlage && <span className="text-xs text-fg-subtle">{p.rechtsgrundlage}</span>}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm font-medium text-fg">
          {p.naechsteFaellig ? formatDatum(p.naechsteFaellig) : 'offen'}
        </p>
        <p className="mt-0.5 text-xs text-fg-subtle">
          {p.tageBisFaellig === null
            ? 'Termin offen'
            : p.tageBisFaellig < 0
              ? `seit ${Math.abs(p.tageBisFaellig)} Tagen überfällig`
              : `in ${p.tageBisFaellig} Tagen fällig`}
        </p>
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ Erfassen */

const feldKlasse =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-accent';

function AnlageFormular({
  objekte,
  gewerke,
  onAngelegt,
  onAbbruch,
}: {
  objekte: ApiObjektRef[];
  gewerke: Auswahl[];
  onAngelegt: (hinweis: string, demoAnlage?: AnlageAnsicht) => void;
  onAbbruch: () => void;
}) {
  const [objektId, setObjektId] = useState(objekte[0]?.id ?? '');
  const [gewerkCode, setGewerkCode] = useState(gewerke[0]?.code ?? 'heizung');
  const [bezeichnung, setBezeichnung] = useState('');
  const [typ, setTyp] = useState('');
  const [hersteller, setHersteller] = useState('');
  const [baujahr, setBaujahr] = useState('');
  const [leistung, setLeistung] = useState('');
  const [brennstoff, setBrennstoff] = useState('');
  const [grossanlage, setGrossanlage] = useState('');
  const [eingebaut, setEingebaut] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (!objektId || !bezeichnung.trim()) {
      setFehler('Bitte ein Objekt wählen und eine Bezeichnung angeben.');
      return;
    }
    setSendet(true);
    try {
      if (DEMO) {
        const gewerk = gewerke.find((g) => g.code === gewerkCode)?.bezeichnung ?? gewerkCode;
        const objekt = objekte.find((o) => o.id === objektId)?.bezeichnung ?? 'Objekt';
        onAngelegt(
          'Anlage erfasst. Im Live-Betrieb werden hier automatisch die passenden Prüffristen abgeleitet.',
          {
            id: `demo-${Date.now()}`,
            objekt,
            bezeichnung: bezeichnung.trim(),
            gewerk,
            typ: typ.trim() || null,
            hersteller: hersteller.trim() || null,
            baujahr: baujahr.trim() ? Number(baujahr) : null,
            standort: null,
            pflichten: [],
          },
        );
        return;
      }
      const antwort = await api.post<{ pflichten: unknown[]; vorschlaege: unknown[] }>('/anlagen', {
        objektId,
        gewerkCode,
        bezeichnung: bezeichnung.trim(),
        typ: typ.trim() || undefined,
        hersteller: hersteller.trim() || undefined,
        baujahr: baujahr.trim() ? Number(baujahr) : undefined,
        leistungKw: leistung.trim() ? Number(leistung.replace(',', '.')) : undefined,
        brennstoff: gewerkCode === 'heizung' ? brennstoff || undefined : undefined,
        // Nicht bekannt wird vorsorglich als Großanlage behandelt (im Zweifel Pflicht).
        twGrossanlage: gewerkCode === 'trinkwasser' && grossanlage ? grossanlage !== 'nein' : undefined,
        eingebautAm: eingebaut.trim() || undefined,
      });
      const anzahl = Array.isArray(antwort.pflichten) ? antwort.pflichten.length : 0;
      const vorschlaege = Array.isArray(antwort.vorschlaege) ? antwort.vorschlaege.length : 0;
      onAngelegt(
        anzahl > 0
          ? `Anlage erfasst — ${anzahl} Prüfpflicht(en) automatisch übernommen und terminiert.` +
              (vorschlaege > 0 ? ` ${vorschlaege} weitere als Vorschlag verfügbar.` : '')
          : 'Anlage erfasst.' +
              (vorschlaege > 0 ? ` ${vorschlaege} mögliche Pflicht(en) stehen als Vorschlag bereit.` : ''),
      );
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Die Anlage konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  return (
    <Card titel="Neue Anlage" className="mb-4">
      <form onSubmit={speichern} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Objekt</span>
            <select className={feldKlasse} value={objektId} onChange={(e) => setObjektId(e.target.value)}>
              {objekte.length === 0 && <option value="">Zuerst ein Objekt anlegen</option>}
              {objekte.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.bezeichnung}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-fg">Gewerk</span>
            <select className={feldKlasse} value={gewerkCode} onChange={(e) => setGewerkCode(e.target.value)}>
              {gewerke.map((g) => (
                <option key={g.code} value={g.code}>
                  {g.bezeichnung}
                </option>
              ))}
            </select>
          </label>
        </div>

        <Input label="Bezeichnung" placeholder="z. B. Gasbrennwertkessel" value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Typ (optional)" value={typ} onChange={(e) => setTyp(e.target.value)} />
          <Input label="Hersteller (optional)" value={hersteller} onChange={(e) => setHersteller(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <Input label="Baujahr" inputMode="numeric" placeholder="optional" value={baujahr} onChange={(e) => setBaujahr(e.target.value)} />
          <Input label="Leistung (kW)" inputMode="decimal" placeholder="optional" value={leistung} onChange={(e) => setLeistung(e.target.value)} />
          {gewerkCode === 'heizung' ? (
            <Select label="Energieträger" value={brennstoff} onChange={(e) => setBrennstoff(e.target.value)}>
              <option value="">Bitte wählen</option>
              <option value="gas">Erdgas</option>
              <option value="fluessiggas">Flüssiggas</option>
              <option value="heizoel">Heizöl</option>
              <option value="fernwaerme">Fernwärme</option>
              <option value="waermepumpe">Wärmepumpe</option>
              <option value="holz">Holz / Pellets</option>
            </Select>
          ) : gewerkCode === 'trinkwasser' ? (
            <Select label="Großanlage (§ 2 TrinkwV)" value={grossanlage} onChange={(e) => setGrossanlage(e.target.value)}>
              <option value="">Bitte wählen</option>
              <option value="ja">Großanlage</option>
              <option value="nein">Keine Großanlage</option>
              <option value="unbekannt">Nicht bekannt</option>
            </Select>
          ) : (
            <div className="hidden sm:block" aria-hidden="true" />
          )}
          <Input label="Eingebaut am" type="date" value={eingebaut} onChange={(e) => setEingebaut(e.target.value)} />
        </div>

        {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={sendet}>
            {sendet ? 'Wird gespeichert …' : 'Anlage speichern'}
          </Button>
          <Button type="button" variante="dezent" onClick={onAbbruch}>
            Abbrechen
          </Button>
        </div>
      </form>
    </Card>
  );
}

export default Anlagenbuch;
